import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { i as isNodeEnvironment } from './isNodeEnvironment.js';
import { g as global$1 } from './global.js';

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

/**
 * lodash (Custom Build) <https://lodash.com/>
 * Build: `lodash modularize exports="npm" -o ./`
 * Copyright jQuery Foundation and other contributors <https://jquery.org/>
 * Released under MIT license <https://lodash.com/license>
 * Based on Underscore.js 1.8.3 <http://underscorejs.org/LICENSE>
 * Copyright Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
 */

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0;

/** `Object#toString` result references. */
var symbolTag = '[object Symbol]';

/** Used to match words composed of alphanumeric characters. */
var reAsciiWord = /[^\x00-\x2f\x3a-\x40\x5b-\x60\x7b-\x7f]+/g;

/** Used to match Latin Unicode letters (excluding mathematical operators). */
var reLatin = /[\xc0-\xd6\xd8-\xf6\xf8-\xff\u0100-\u017f]/g;

/** Used to compose unicode character classes. */
var rsAstralRange = '\\ud800-\\udfff',
    rsComboMarksRange = '\\u0300-\\u036f\\ufe20-\\ufe23',
    rsComboSymbolsRange = '\\u20d0-\\u20f0',
    rsDingbatRange = '\\u2700-\\u27bf',
    rsLowerRange = 'a-z\\xdf-\\xf6\\xf8-\\xff',
    rsMathOpRange = '\\xac\\xb1\\xd7\\xf7',
    rsNonCharRange = '\\x00-\\x2f\\x3a-\\x40\\x5b-\\x60\\x7b-\\xbf',
    rsPunctuationRange = '\\u2000-\\u206f',
    rsSpaceRange = ' \\t\\x0b\\f\\xa0\\ufeff\\n\\r\\u2028\\u2029\\u1680\\u180e\\u2000\\u2001\\u2002\\u2003\\u2004\\u2005\\u2006\\u2007\\u2008\\u2009\\u200a\\u202f\\u205f\\u3000',
    rsUpperRange = 'A-Z\\xc0-\\xd6\\xd8-\\xde',
    rsVarRange = '\\ufe0e\\ufe0f',
    rsBreakRange = rsMathOpRange + rsNonCharRange + rsPunctuationRange + rsSpaceRange;

/** Used to compose unicode capture groups. */
var rsApos = "['\u2019]",
    rsBreak = '[' + rsBreakRange + ']',
    rsCombo = '[' + rsComboMarksRange + rsComboSymbolsRange + ']',
    rsDigits = '\\d+',
    rsDingbat = '[' + rsDingbatRange + ']',
    rsLower = '[' + rsLowerRange + ']',
    rsMisc = '[^' + rsAstralRange + rsBreakRange + rsDigits + rsDingbatRange + rsLowerRange + rsUpperRange + ']',
    rsFitz = '\\ud83c[\\udffb-\\udfff]',
    rsModifier = '(?:' + rsCombo + '|' + rsFitz + ')',
    rsNonAstral = '[^' + rsAstralRange + ']',
    rsRegional = '(?:\\ud83c[\\udde6-\\uddff]){2}',
    rsSurrPair = '[\\ud800-\\udbff][\\udc00-\\udfff]',
    rsUpper = '[' + rsUpperRange + ']',
    rsZWJ = '\\u200d';

/** Used to compose unicode regexes. */
var rsLowerMisc = '(?:' + rsLower + '|' + rsMisc + ')',
    rsUpperMisc = '(?:' + rsUpper + '|' + rsMisc + ')',
    rsOptLowerContr = '(?:' + rsApos + '(?:d|ll|m|re|s|t|ve))?',
    rsOptUpperContr = '(?:' + rsApos + '(?:D|LL|M|RE|S|T|VE))?',
    reOptMod = rsModifier + '?',
    rsOptVar = '[' + rsVarRange + ']?',
    rsOptJoin = '(?:' + rsZWJ + '(?:' + [rsNonAstral, rsRegional, rsSurrPair].join('|') + ')' + rsOptVar + reOptMod + ')*',
    rsSeq = rsOptVar + reOptMod + rsOptJoin,
    rsEmoji = '(?:' + [rsDingbat, rsRegional, rsSurrPair].join('|') + ')' + rsSeq;

/** Used to match apostrophes. */
var reApos = RegExp(rsApos, 'g');

/**
 * Used to match [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks) and
 * [combining diacritical marks for symbols](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks_for_Symbols).
 */
var reComboMark = RegExp(rsCombo, 'g');

/** Used to match complex or compound words. */
var reUnicodeWord = RegExp([
  rsUpper + '?' + rsLower + '+' + rsOptLowerContr + '(?=' + [rsBreak, rsUpper, '$'].join('|') + ')',
  rsUpperMisc + '+' + rsOptUpperContr + '(?=' + [rsBreak, rsUpper + rsLowerMisc, '$'].join('|') + ')',
  rsUpper + '?' + rsLowerMisc + '+' + rsOptLowerContr,
  rsUpper + '+' + rsOptUpperContr,
  rsDigits,
  rsEmoji
].join('|'), 'g');

/** Used to detect strings that need a more robust regexp to match words. */
var reHasUnicodeWord = /[a-z][A-Z]|[A-Z]{2,}[a-z]|[0-9][a-zA-Z]|[a-zA-Z][0-9]|[^a-zA-Z0-9 ]/;

/** Used to map Latin Unicode letters to basic Latin letters. */
var deburredLetters = {
  // Latin-1 Supplement block.
  '\xc0': 'A',  '\xc1': 'A', '\xc2': 'A', '\xc3': 'A', '\xc4': 'A', '\xc5': 'A',
  '\xe0': 'a',  '\xe1': 'a', '\xe2': 'a', '\xe3': 'a', '\xe4': 'a', '\xe5': 'a',
  '\xc7': 'C',  '\xe7': 'c',
  '\xd0': 'D',  '\xf0': 'd',
  '\xc8': 'E',  '\xc9': 'E', '\xca': 'E', '\xcb': 'E',
  '\xe8': 'e',  '\xe9': 'e', '\xea': 'e', '\xeb': 'e',
  '\xcc': 'I',  '\xcd': 'I', '\xce': 'I', '\xcf': 'I',
  '\xec': 'i',  '\xed': 'i', '\xee': 'i', '\xef': 'i',
  '\xd1': 'N',  '\xf1': 'n',
  '\xd2': 'O',  '\xd3': 'O', '\xd4': 'O', '\xd5': 'O', '\xd6': 'O', '\xd8': 'O',
  '\xf2': 'o',  '\xf3': 'o', '\xf4': 'o', '\xf5': 'o', '\xf6': 'o', '\xf8': 'o',
  '\xd9': 'U',  '\xda': 'U', '\xdb': 'U', '\xdc': 'U',
  '\xf9': 'u',  '\xfa': 'u', '\xfb': 'u', '\xfc': 'u',
  '\xdd': 'Y',  '\xfd': 'y', '\xff': 'y',
  '\xc6': 'Ae', '\xe6': 'ae',
  '\xde': 'Th', '\xfe': 'th',
  '\xdf': 'ss',
  // Latin Extended-A block.
  '\u0100': 'A',  '\u0102': 'A', '\u0104': 'A',
  '\u0101': 'a',  '\u0103': 'a', '\u0105': 'a',
  '\u0106': 'C',  '\u0108': 'C', '\u010a': 'C', '\u010c': 'C',
  '\u0107': 'c',  '\u0109': 'c', '\u010b': 'c', '\u010d': 'c',
  '\u010e': 'D',  '\u0110': 'D', '\u010f': 'd', '\u0111': 'd',
  '\u0112': 'E',  '\u0114': 'E', '\u0116': 'E', '\u0118': 'E', '\u011a': 'E',
  '\u0113': 'e',  '\u0115': 'e', '\u0117': 'e', '\u0119': 'e', '\u011b': 'e',
  '\u011c': 'G',  '\u011e': 'G', '\u0120': 'G', '\u0122': 'G',
  '\u011d': 'g',  '\u011f': 'g', '\u0121': 'g', '\u0123': 'g',
  '\u0124': 'H',  '\u0126': 'H', '\u0125': 'h', '\u0127': 'h',
  '\u0128': 'I',  '\u012a': 'I', '\u012c': 'I', '\u012e': 'I', '\u0130': 'I',
  '\u0129': 'i',  '\u012b': 'i', '\u012d': 'i', '\u012f': 'i', '\u0131': 'i',
  '\u0134': 'J',  '\u0135': 'j',
  '\u0136': 'K',  '\u0137': 'k', '\u0138': 'k',
  '\u0139': 'L',  '\u013b': 'L', '\u013d': 'L', '\u013f': 'L', '\u0141': 'L',
  '\u013a': 'l',  '\u013c': 'l', '\u013e': 'l', '\u0140': 'l', '\u0142': 'l',
  '\u0143': 'N',  '\u0145': 'N', '\u0147': 'N', '\u014a': 'N',
  '\u0144': 'n',  '\u0146': 'n', '\u0148': 'n', '\u014b': 'n',
  '\u014c': 'O',  '\u014e': 'O', '\u0150': 'O',
  '\u014d': 'o',  '\u014f': 'o', '\u0151': 'o',
  '\u0154': 'R',  '\u0156': 'R', '\u0158': 'R',
  '\u0155': 'r',  '\u0157': 'r', '\u0159': 'r',
  '\u015a': 'S',  '\u015c': 'S', '\u015e': 'S', '\u0160': 'S',
  '\u015b': 's',  '\u015d': 's', '\u015f': 's', '\u0161': 's',
  '\u0162': 'T',  '\u0164': 'T', '\u0166': 'T',
  '\u0163': 't',  '\u0165': 't', '\u0167': 't',
  '\u0168': 'U',  '\u016a': 'U', '\u016c': 'U', '\u016e': 'U', '\u0170': 'U', '\u0172': 'U',
  '\u0169': 'u',  '\u016b': 'u', '\u016d': 'u', '\u016f': 'u', '\u0171': 'u', '\u0173': 'u',
  '\u0174': 'W',  '\u0175': 'w',
  '\u0176': 'Y',  '\u0177': 'y', '\u0178': 'Y',
  '\u0179': 'Z',  '\u017b': 'Z', '\u017d': 'Z',
  '\u017a': 'z',  '\u017c': 'z', '\u017e': 'z',
  '\u0132': 'IJ', '\u0133': 'ij',
  '\u0152': 'Oe', '\u0153': 'oe',
  '\u0149': "'n", '\u017f': 'ss'
};

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = freeGlobal || freeSelf || Function('return this')();

/**
 * A specialized version of `_.reduce` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @param {*} [accumulator] The initial value.
 * @param {boolean} [initAccum] Specify using the first element of `array` as
 *  the initial value.
 * @returns {*} Returns the accumulated value.
 */
function arrayReduce(array, iteratee, accumulator, initAccum) {
  var index = -1,
      length = array ? array.length : 0;

  if (initAccum && length) {
    accumulator = array[++index];
  }
  while (++index < length) {
    accumulator = iteratee(accumulator, array[index], index, array);
  }
  return accumulator;
}

/**
 * Splits an ASCII `string` into an array of its words.
 *
 * @private
 * @param {string} The string to inspect.
 * @returns {Array} Returns the words of `string`.
 */
function asciiWords(string) {
  return string.match(reAsciiWord) || [];
}

/**
 * The base implementation of `_.propertyOf` without support for deep paths.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Function} Returns the new accessor function.
 */
function basePropertyOf(object) {
  return function(key) {
    return object == null ? undefined : object[key];
  };
}

/**
 * Used by `_.deburr` to convert Latin-1 Supplement and Latin Extended-A
 * letters to basic Latin letters.
 *
 * @private
 * @param {string} letter The matched letter to deburr.
 * @returns {string} Returns the deburred letter.
 */
var deburrLetter = basePropertyOf(deburredLetters);

/**
 * Checks if `string` contains a word composed of Unicode symbols.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {boolean} Returns `true` if a word is found, else `false`.
 */
function hasUnicodeWord(string) {
  return reHasUnicodeWord.test(string);
}

/**
 * Splits a Unicode `string` into an array of its words.
 *
 * @private
 * @param {string} The string to inspect.
 * @returns {Array} Returns the words of `string`.
 */
function unicodeWords(string) {
  return string.match(reUnicodeWord) || [];
}

/** Used for built-in method references. */
var objectProto = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var objectToString = objectProto.toString;

/** Built-in value references. */
var Symbol = root.Symbol;

/** Used to convert symbols to primitives and strings. */
var symbolProto = Symbol ? Symbol.prototype : undefined,
    symbolToString = symbolProto ? symbolProto.toString : undefined;

/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isSymbol(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
}

/**
 * Creates a function like `_.camelCase`.
 *
 * @private
 * @param {Function} callback The function to combine each word.
 * @returns {Function} Returns the new compounder function.
 */
function createCompounder(callback) {
  return function(string) {
    return arrayReduce(words(deburr(string).replace(reApos, '')), callback, '');
  };
}

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return !!value && typeof value == 'object';
}

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' ||
    (isObjectLike(value) && objectToString.call(value) == symbolTag);
}

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString(value) {
  return value == null ? '' : baseToString(value);
}

/**
 * Deburrs `string` by converting
 * [Latin-1 Supplement](https://en.wikipedia.org/wiki/Latin-1_Supplement_(Unicode_block)#Character_table)
 * and [Latin Extended-A](https://en.wikipedia.org/wiki/Latin_Extended-A)
 * letters to basic Latin letters and removing
 * [combining diacritical marks](https://en.wikipedia.org/wiki/Combining_Diacritical_Marks).
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category String
 * @param {string} [string=''] The string to deburr.
 * @returns {string} Returns the deburred string.
 * @example
 *
 * _.deburr('déjà vu');
 * // => 'deja vu'
 */
function deburr(string) {
  string = toString(string);
  return string && string.replace(reLatin, deburrLetter).replace(reComboMark, '');
}

/**
 * Converts `string` to
 * [kebab case](https://en.wikipedia.org/wiki/Letter_case#Special_case_styles).
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category String
 * @param {string} [string=''] The string to convert.
 * @returns {string} Returns the kebab cased string.
 * @example
 *
 * _.kebabCase('Foo Bar');
 * // => 'foo-bar'
 *
 * _.kebabCase('fooBar');
 * // => 'foo-bar'
 *
 * _.kebabCase('__FOO_BAR__');
 * // => 'foo-bar'
 */
var kebabCase = createCompounder(function(result, word, index) {
  return result + (index ? '-' : '') + word.toLowerCase();
});

/**
 * Splits `string` into an array of its words.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category String
 * @param {string} [string=''] The string to inspect.
 * @param {RegExp|string} [pattern] The pattern to match words.
 * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
 * @returns {Array} Returns the words of `string`.
 * @example
 *
 * _.words('fred, barney, & pebbles');
 * // => ['fred', 'barney', 'pebbles']
 *
 * _.words('fred, barney, & pebbles', /[^, ]+/g);
 * // => ['fred', 'barney', '&', 'pebbles']
 */
function words(string, pattern, guard) {
  string = toString(string);
  pattern = guard ? undefined : pattern;

  if (pattern === undefined) {
    return hasUnicodeWord(string) ? unicodeWords(string) : asciiWords(string);
  }
  return string.match(pattern) || [];
}

var lodash_kebabcase = kebabCase;

// Format member name based on the deprecation type
const formatMemberName = (name, type) => {
    switch (type) {
        case 'property':
            return lodash_kebabcase(name);
        case 'method':
            return `${name}()`;
        default:
            return name;
    }
};
// Return the global document's styleSheets, if available
const getStyleSheets = () => {
    if (isNodeEnvironment()) {
        return;
    }
    return global$1.document.styleSheets;
};
// Filter global style sheets to prevent CORS-related DOMExceptions
const filterStyleSheets = (styleSheets) => {
    return Array.from(styleSheets).filter((styleSheet) => !styleSheet.href || styleSheet.href.startsWith(location.origin));
};
// Get applied component parts from the given component and stylesheets
const getAppliedComponentParts = (component, styleSheets) => {
    const parts = [];
    if (!(styleSheets === null || styleSheets === void 0 ? void 0 : styleSheets.length)) {
        return parts;
    }
    for (const sheet of styleSheets) {
        if (!('cssRules' in sheet)) {
            continue;
        }
        for (const rule of Object.values(sheet.cssRules)) {
            if (!('selectorText' in rule) || !rule.selectorText.includes('::part')) {
                continue;
            }
            rule.selectorText.split(',').forEach((selectorText) => {
                selectorText = selectorText.trim();
                if (!selectorText.includes('::part')) {
                    return;
                }
                const [selector, part] = selectorText.split('::part');
                if (component.matches(selector)) {
                    parts.push(part
                        // Remove parentheses from the part selector
                        .replace(/[{()}]/g, ''));
                }
            });
        }
    }
    // Remove duplicates and return sorted array
    return [...new Set(parts)].sort();
};

class DeprecationLogger {
    constructor(componentName, deprecationMap) {
        this.componentName = componentName;
        this.deprecationMap = deprecationMap;
    }
    getDeprecationConfig(name, type) {
        var _a;
        return ((_a = this.deprecationMap[name]) === null || _a === void 0 ? void 0 : _a.type) === type ? this.deprecationMap[name] : undefined;
    }
    createDeprecationMessage({ type, name, message }) {
        const header = type === 'component' ? `Deprecated ${type}: "${name}"` : `Deprecated ${type} in ${this.componentName}: "${name}"`;
        return `${header}
${message}
See the documentation for more information: https://win.gs/CosmosWebDeprecations`;
    }
    logWarning(args) {
        logger$1.warn(this.createDeprecationMessage(Object.assign(Object.assign({}, args), { componentName: this.componentName })));
    }
    warnComponent() {
        const type = 'component';
        const config = this.getDeprecationConfig(this.componentName, type);
        if (!config) {
            return;
        }
        this.logWarning(Object.assign(Object.assign({}, config), { name: this.componentName, type }));
    }
    warnMethod(methodName, args) {
        const type = 'method';
        const config = this.getDeprecationConfig(methodName, type);
        if (!config || (config.warnCondition && !config.warnCondition(args))) {
            return;
        }
        this.logWarning(Object.assign(Object.assign({}, config), { name: formatMemberName(methodName, type) }));
    }
    warnProperty(propertyName, value) {
        let type = 'property';
        const config = this.getDeprecationConfig(propertyName, type);
        if (!config || (config.warnCondition && !config.warnCondition(value))) {
            return;
        }
        let name = formatMemberName(propertyName, type);
        if (config.deprecatedValues) {
            if (!config.deprecatedValues.includes(value)) {
                return;
            }
            type = 'property value';
            name = String(value);
        }
        this.logWarning(Object.assign(Object.assign({}, config), { name,
            type }));
    }
    warnProperties(componentInstance) {
        for (const name in this.deprecationMap) {
            const value = componentInstance[name];
            if (typeof value === 'undefined' || this.deprecationMap[name].type !== 'property') {
                continue;
            }
            this.warnProperty(name, value);
        }
    }
    warnEvent(eventName) {
        const type = 'event';
        const config = this.getDeprecationConfig(eventName, type);
        if (!config) {
            return;
        }
        this.logWarning(Object.assign(Object.assign({}, config), { name: formatMemberName(eventName, type) }));
    }
    warnPart(partName) {
        const type = 'part';
        const config = this.getDeprecationConfig(partName, type);
        if (!config) {
            return;
        }
        this.logWarning(Object.assign(Object.assign({}, config), { name: formatMemberName(partName, type) }));
    }
    warnParts(component) {
        const styleSheets = getStyleSheets();
        if (!styleSheets) {
            return;
        }
        const appliedParts = getAppliedComponentParts(component, filterStyleSheets(styleSheets));
        for (const part of appliedParts) {
            if (!(part in this.deprecationMap)) {
                continue;
            }
            this.warnPart(part);
        }
    }
}

/**
 * Handle console logs and warnings to the browser
 *
 * When used in a component, unit tests may throw an error saying
 * console has no warn methods. To fix simply mock this module by
 * adding the following line at the top of your test file:
 * `jest.mock('__PATH_TO_UTILS__/utils/logger/logger);`
 */
/**
 * Display a message as a warning in the browser console.
 *
 * @param {string} message The message to display as a warning.
 * @returns {void}
 * */
const warn = (message) => {
    if (config$1.disableWarnings) {
        return;
    }
    global$1.console.warn('%c🪐 Cosmos', 'color: #000F1E;background:#FF900099;font-weight: bold; font-size:10px; padding:2px 6px; border-radius: 5px', message);
};
const logger$1 = { warn };

var _a;
let disableWarnings = !!((_a = global$1.cosmosConfig) === null || _a === void 0 ? void 0 : _a.disableWarnings);
/**
 * Global config
 * @typedef {Object} Config
 * @property {boolean} disableWarnings - Flag to disable console warnings
 */
const config = {
    get disableWarnings() {
        return disableWarnings;
    },
    set disableWarnings(value) {
        disableWarnings = value;
    },
};
// Makes the config available
global$1.cosmosConfig = config;
const config$1 = config;

const logger = new DeprecationLogger('deprecated-component', {
    'obsoleteProp': {
        type: 'property',
        message: '`obsoleteProp` will be removed.',
        resolveDate: 'June, XXXX',
    },
    'replacedProp': {
        type: 'property',
        message: '`replacedProp` will be replaced by `newProp`.',
        resolveDate: 'June, XXXX',
    },
    'updatedPropValue': {
        type: 'property',
        message: 'The value "old-value" for the property `updatedPropValue` will be replaced by "new-value".',
        warnCondition: (value) => {
            return value === 'old-value';
        },
        resolveDate: 'June, XXXX',
    },
    'deprecatedPropValue': {
        type: 'property',
        message: 'The property values "deprecated-value-a" and "deprecated-value-b" for the property `deprecatedPropValue` will be removed.',
        resolveDate: 'June, XXXX',
        deprecatedValues: ['deprecated-value-a', 'deprecated-value-b'],
    },
    'obsolete-event': {
        type: 'event',
        message: '`obsolete-event` will be removed.',
        resolveDate: 'June, XXXX',
    },
    'replaced-event': {
        type: 'event',
        message: '`replaced-event` will be removed.',
        resolveDate: 'June, XXXX',
    },
    'obsoleteMethod': {
        type: 'method',
        message: '`obsoleteMethod` will be removed.',
        resolveDate: 'June, XXXX',
    },
    'deprecated-part': {
        type: 'part',
        message: '`deprecated-part` will be removed.',
        resolveDate: 'June, XXXX',
    },
});
const DeprecatedComponent$1 = /*@__PURE__*/ proxyCustomElement(class DeprecatedComponent extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.newEvent = createEvent(this, "new-event", 7);
        this.obsoleteEvent = createEvent(this, "obsolete-event", 7);
        this.replacedEvent = createEvent(this, "replaced-event", 7);
        this.deprecatedPropValue = 'value-c';
        this.newProp = undefined;
        this.obsoleteProp = undefined;
        this.replacedProp = undefined;
        this.updatedPropValue = 'new-value';
    }
    /**
     * description
     * @deprecated deprecation message
     */
    async obsoleteMethod() {
        logger.warnMethod('obsoleteMethod');
    }
    obsoleteEventHandler() {
        logger.warnEvent('obsolete-event');
    }
    replacedEventHandler() {
        logger.warnEvent('replaced-event');
    }
    watchObsoleteProp() {
        logger.warnProperty('obsoleteProp');
    }
    watchToBeReplacedProp() {
        logger.warnProperty('replacedProp');
    }
    watchUpdatedPropValue(newValue) {
        logger.warnProperty('updatedPropValue', newValue);
    }
    watchDeprecatedPropValue(newValue) {
        logger.warnProperty('deprecatedPropValue', newValue);
    }
    componentDidLoad() {
        logger.warnProperties(this);
        logger.warnParts(this.element);
    }
    render() {
        return h("div", { class: "deprecated-component", part: "deprecated-part new-part" });
    }
    get element() { return this; }
    static get watchers() { return {
        "obsoleteProp": ["watchObsoleteProp"],
        "replacedProp": ["watchToBeReplacedProp"],
        "updatedPropValue": ["watchUpdatedPropValue"],
        "deprecatedPropValue": ["watchDeprecatedPropValue"]
    }; }
}, [1, "deprecated-component", {
        "deprecatedPropValue": [513, "deprecated-prop-value"],
        "newProp": [513, "new-prop"],
        "obsoleteProp": [513, "obsolete-prop"],
        "replacedProp": [513, "replaced-prop"],
        "updatedPropValue": [513, "updated-prop-value"],
        "obsoleteMethod": [64]
    }, [[0, "obsolete-event", "obsoleteEventHandler"], [0, "replaced-event", "replacedEventHandler"]], {
        "obsoleteProp": ["watchObsoleteProp"],
        "replacedProp": ["watchToBeReplacedProp"],
        "updatedPropValue": ["watchUpdatedPropValue"],
        "deprecatedPropValue": ["watchDeprecatedPropValue"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["deprecated-component"];
    components.forEach(tagName => { switch (tagName) {
        case "deprecated-component":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, DeprecatedComponent$1);
            }
            break;
    } });
}

const DeprecatedComponent = DeprecatedComponent$1;
const defineCustomElement = defineCustomElement$1;

export { DeprecatedComponent, config$1 as c, defineCustomElement };
